import { useCallback } from 'react';
import axios from 'axios';
import { useTelegram } from './useTelegram';
import { Navigate, useNavigate } from 'react-router-dom';


// API base URL - using environment variable
console.log(process.env)
const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:3001/api' : 'https://arbifoxapi.the-cicada.com/api';

console.log('API_URL->', API_URL)
// Создаем глобальный инстанс axios для перехвата 401 ошибок
const apiInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },

  // добавляй в куки в значение access_token данные из локального хранилища arbifox_token

  withCredentials: true // Включаем отправку cookies с запросами
});

// Перехватчик ответов для обработки 401 ошибок
apiInstance.interceptors.response.use(
  response => response, // Пропускаем успешные ответы
  error => {
    // Проверяем, является ли ошибка 401 Unauthorized
    if (error.response && error.response.status === 401) {
      // В режиме разработки просто логируем ошибку, не выбрасываем исключение
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ 401 Unauthorized in development mode:', error.config?.url);
        // Возвращаем ошибку для обработки в компонентах
        return Promise.reject(error);
      } else {
        // В продакшене выбрасываем ошибку для перенаправления
        const unauthorizedError = new Error('Unauthorized access detected, redirecting to unauthorized page');
        unauthorizedError.response = error.response;
        return Promise.reject(unauthorizedError);
      }
    }

    // Возвращаем ошибку для дальнейшей обработки
    return Promise.reject(error);
  }
);

export function useApi() {
  const { tgData, tgWebApp } = useTelegram();

  // Используем глобальный инстанс
  const api = apiInstance;

  // Add Telegram auth data to every request
  api.interceptors.request.use(config => {
    // If we have Telegram initData, add it to the request
    if (tgData) {
      // Add the raw initData string if available
      if (tgWebApp?.initData) {
        config.headers['X-Telegram-Data'] = tgWebApp.initData;
      }

      // Add user ID if available
      if (tgData.user?.id) {
        config.headers['X-Telegram-User-ID'] = tgData.user.id.toString();
      }
    }
    return config;
  });

  // Function to validate Telegram WebApp data
  const validateTelegramData = useCallback(async () => {
    try {
      // If we're in development mode and no Telegram data is available, simulate success
      if (process.env.NODE_ENV === 'development' && !tgWebApp?.initData) {
        console.log('Development mode: Simulating successful Telegram data validation');
        return { success: true, message: 'Validation skipped in development mode' };
      }

      // Make sure we have initData to validate
      if (!tgWebApp?.initData) {
        throw new Error('No Telegram WebApp initData available to validate');
      }

      // Send the initData to our backend for validation
      const response = await api.post('/auth/validate-telegram-data', {
        initData: tgWebApp.initData
      });

      console.log('Telegram data validation response:', response.data);

      // Теперь токен сохраняется автоматически как cookie, но мы также можем использовать его для локального хранения при необходимости
      if (response.data.token) {
        localStorage.setItem('arbifox_token', response.data.token);
      }

      return response.data;
    } catch (error) {
      console.error('Error validating Telegram data:', error);
      throw error;
    }
  }, [api, tgWebApp]);

  // Функция для проверки валидности токена
  const verifyToken = useCallback(async () => {
    try {
      const response = await api.get('/auth/verify-token');
      return response.data;
    } catch (error) {
      console.error('Error verifying token:', error);
      // Ошибка уже будет обработана глобальным перехватчиком, если это 401
      throw error;
    }
  }, [api]);

  // For now, let's use mock data for API responses
  const useMockData = false; // Changed to false to ensure real API connections are made

  // User-related API calls
  const getUserData = useCallback(async () => {
    try {
      // In development mode, we might still want to validate Telegram data
      if (process.env.NODE_ENV !== 'development') {
        try {
          await validateTelegramData();
        } catch (validationError) {
          console.error('Telegram data validation failed:', validationError);
          throw validationError;
        }
      }

      // Use real API data
      const response = await api.get('/user');
      // console.log('User data from API:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching user data:', error);
      throw error;
    }
  }, [api, validateTelegramData]);

  // Transaction-related API calls
  const getTransactions = useCallback(async (filters = {}) => {
    try {
      if (useMockData) {
        // Return mock transaction data
        console.log('Using mock transaction data with filters:', filters);

        const mockTransactions = [
          {
            id: 1,
            type: 'deposit',
            amount: 200,
            currency: 'USDT',
            date: '2025-02-01T12:00:00',
            status: 'confirmed',
            displayName: 'Пополнение'
          },
          {
            id: 2,
            type: 'license',
            amount: 150,
            currency: 'USDT',
            date: '2025-02-01T12:30:00',
            status: 'success',
            displayName: 'Покупка лицензии',
            licenseType: 'Basic'
          },
          {
            id: 3,
            type: 'withdraw',
            amount: 50,
            currency: 'USDT',
            date: '2025-02-01T11:30:00',
            status: 'success',
            displayName: 'Вывод'
          },
          {
            id: 4,
            type: 'reinvest',
            amount: 10,
            currency: 'USDT',
            date: '2025-02-05T11:50:00',
            status: 'success',
            displayName: 'Реинвестирование'
          },
          {
            id: 5,
            type: 'license-upgrade',
            amount: 1250,
            currency: 'USDT',
            date: '2025-02-10T11:50:00',
            status: 'success',
            displayName: 'Улучшение лицензии',
            licenseType: 'Standart'
          }
        ];

        // Apply filters if they exist
        if (filters.type) {
          return mockTransactions.filter(t => t.type === filters.type);
        }

        return mockTransactions;
      }

      const response = await api.get('/transactions', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }, [api]);

  // Notification-related API calls
  const getNotifications = useCallback(async (showRead = true) => {
    try {
      if (useMockData) {
        // Return mock notifications
        // console.log('Using mock notifications');
        return {
          notifications: [
            {
              _id: '1',
              title: 'Автоматизированный режим арбитража завершен!',
              message: 'Завершено 3 сделки, общая прибыль составила $30. Отличная работа!',
              type: 'arbitrage',
              read: false,
              createdAt: new Date().toISOString()
            },
            {
              _id: '2',
              title: 'Пополнение баланса',
              message: 'Ваш баланс пополнен на 200 USDT. Текущий баланс: 300 USDT',
              type: 'transaction',
              read: true,
              createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
            }
          ],
          unreadCount: 1
        };
      }

      // console.log(`Fetching notifications with showRead=${showRead}`);
      const response = await api.get('/user/notifications', {
        params: { showRead: showRead.toString() }
      });
      // console.log('Notifications API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }, [api]);

  // Функция для инициализации сервиса уведомлений при старте приложения
  const initNotificationService = useCallback(async (showRead = false) => {
    try {
      // console.log('Initializing notification service connection');
      // Запрашиваем непрочитанные уведомления при старте
      const data = await getNotifications(showRead);
      // console.log('Notification service initialized with data:', data);
      return data;
    } catch (error) {
      console.error('Failed to initialize notification service:', error);
      // В случае ошибки, возвращаем пустой объект с нулевым счетчиком
      return { notifications: [], unreadCount: 0 };
    }
  }, [getNotifications]);

  const markNotificationAsRead = useCallback(async (notificationId) => {
    try {
      if (useMockData) {
        // Simulate marking a notification as read
        console.log('Simulating marking notification as read:', notificationId);
        return {
          success: true,
          notification: {
            _id: notificationId,
            read: true
          }
        };
      }

      const response = await api.put(`/user/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }, [api]);

  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      if (useMockData) {
        // Simulate marking all notifications as read
        console.log('Simulating marking all notifications as read');
        return {
          success: true,
          message: 'All notifications marked as read',
          count: 2
        };
      }

      const response = await api.put('/user/notifications/read-all');
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }, [api]);

  const createDeposit = useCallback(async (amount, currency = 'USDT') => {
    try {
      if (useMockData) {
        // Simulate a successful deposit
        console.log('Simulating deposit:', { amount, currency });
        const paymentUrl = `https://mockpayment.example.com/pay/${Math.random().toString(36).substring(2, 15)}`;
        return {
          success: true,
          message: 'Запрос на пополнение создан',
          transaction: {
            id: Math.floor(Math.random() * 1000000),
            type: 'DEPOSIT',
            amount,
            currency,
            status: 'PENDING',
            createdAt: new Date().toISOString(),
            details: {
              paymentUrl
            }
          }
        };
      }

      const response = await api.post('/transactions/deposit', { amount, currency });
      return response.data;
    } catch (error) {
      console.error('Error creating deposit:', error);
      throw error;
    }
  }, [api]);

  const requestWithdrawal = useCallback(async (amount, currency, address) => {
    try {
      if (useMockData) {
        // Simulate a successful withdrawal request
        console.log('Simulating withdrawal:', { amount, currency, address });
        return {
          id: Math.floor(Math.random() * 1000000),
          amount,
          currency,
          address,
          status: 'pending',
          createdAt: new Date().toISOString()
        };
      }

      const response = await api.post('/transactions/withdraw', {
        amount,
        currency,
        address
      });
      return response.data;
    } catch (error) {
      console.error('Error requesting withdrawal:', error);
      throw error;
    }
  }, [api]);

  // Arbitrage-related API calls
  const getArbitrageOpportunities = useCallback(async (isReload = false) => {
    console.log('getArbitrageOpportunities called with isReload:', isReload);
    try {
      if (useMockData) {
        // Return mock arbitrage opportunities
        console.log('Using mock arbitrage opportunities');
        return [
          {
            id: 1,
            pair: 'BTCUSDT',
            sourceExchange: 'Binance',
            targetExchange: 'Kraken',
            sourcePrice: 65000,
            targetPrice: 65390,
            profitPercent: 0.6,
            timestamp: new Date().toISOString()
          },
          {
            id: 2,
            pair: 'ETHUSDT',
            sourceExchange: 'Coinbase',
            targetExchange: 'Bitfinex',
            sourcePrice: 3400,
            targetPrice: 3430,
            profitPercent: 0.88,
            timestamp: new Date().toISOString()
          }
        ];
      }

      // Добавляем параметр isReload в URL только если он true
      const params = isReload ? { isReload: true } : {};
      console.log('Making API request with params:', params);
      const response = await api.get(`/arbitrage/opportunities${isReload ? '?isReload=true' : ''}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching arbitrage opportunities:', error);
      throw error;
    }
  }, [api]);

  const executeArbitrage = useCallback(async (operationData) => {
    try {
      if (useMockData) {
        // Simulate a successful arbitrage operation
        console.log('Simulating arbitrage execution:', operationData);
        return {
          id: Math.floor(Math.random() * 1000000),
          ...operationData,
          status: 'completed',
          profit: operationData.amount * (operationData.profitPercent / 100),
          executedAt: new Date().toISOString()
        };
      }

      const response = await api.post('/arbitrage/execute', operationData);
      return response.data;
    } catch (error) {
      console.error('Error executing arbitrage:', error);
      throw error;
    }
  }, [api]);

  const getArbitrageHistory = useCallback(async () => {
    try {
      if (useMockData) {
        // Return mock arbitrage history
        console.log('Using mock arbitrage history');
        return [
          {
            id: 1,
            date: new Date("2025-02-01T12:30:00"),
            pair: "BTCUSDT",
            sourceExchange: "Binance",
            targetExchange: "Coinbase",
            profit: 15.00,
            profitPercent: 0.24
          },
          {
            id: 2,
            date: new Date("2025-02-02T14:00:00"),
            pair: "ETHUSDT",
            sourceExchange: "Kraken",
            targetExchange: "Bitfinex",
            profit: 10.50,
            profitPercent: 0.17
          }
        ];
      }

      const response = await api.get('/arbitrage/history');
      return response.data;
    } catch (error) {
      console.error('Error fetching arbitrage history:', error);
      throw error;
    }
  }, [api]);

  // Licences API calls
  const getLicenses = useCallback(async () => {
    try {
      if (useMockData) {
        // Return mock license data
        console.log('Using mock license data');
        return {
          active_licenses: [
            {
              id: 'basic123',
              type: 'Basic',
              status: 'active',
              expiry_date: '2025-03-01T00:00:00',
              activation_date: '2025-02-01T00:00:00',
              price: 150
            }
          ],
          expired_licenses: []
        };
      }

      const response = await api.get('/licenses');
      return response.data;
    } catch (error) {
      console.error('Error fetching licenses:', error);
      throw error;
    }
  }, [api]);

  // Referral API calls
  const getReferralInfo = useCallback(async () => {
    try {
      if (useMockData) {
        // Return mock referral data
        console.log('Using mock referral data');
        return {
          referralLink: `https://t.me/arbifox/startapp=rc_${new Date().getTime().toString(36)}`,
          totalReferrals: 25,
          totalDeposit: 8000,
          totalProfit: 500,
          levels: [
            {
              level: 1,
              count: 5,
              deposit: 3000,
              profit: 150
            },
            {
              level: 2,
              count: 10,
              deposit: 2500,
              profit: 125
            },
            {
              level: 3,
              count: 8,
              deposit: 1800,
              profit: 90
            },
            {
              level: 4,
              count: 2,
              deposit: 500,
              profit: 25
            },
            {
              level: 5,
              count: 0,
              deposit: 0,
              profit: 0
            }
          ]
        };
      }

      const response = await api.get('/referrals');
      return response.data;
    } catch (error) {
      console.error('Error fetching referral info:', error);
      throw error;
    }
  }, [api]);

  const getLevelReferrals = useCallback(async ({ level, page = 1, limit = 100, sortBy = 'registrationDate', sortOrder = 'desc' }) => {
    try {
      if (useMockData) {
        // Return mock level referrals data
        console.log(`Using mock referrals data for level ${level}, page ${page}`);
        
        const mockReferrals = Array.from({ length: Math.min(limit, 25) }, (_, i) => ({
          id: `ref_${level}_${(page - 1) * limit + i + 1}`,
          username: `User${level}${i + 1}`,
          firstName: `Пользователь`,
          lastName: `${level}.${i + 1}`,
          avatar: null, // Will use default avatar
          deposit: Math.random() * 5000,
          profit: Math.random() * 500,
          registrationDate: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString()
        }));

        // Sort the mock data
        mockReferrals.sort((a, b) => {
          let aVal, bVal;
          switch (sortBy) {
            case 'deposit':
              aVal = a.deposit;
              bVal = b.deposit;
              break;
            case 'profit':
              aVal = a.profit;
              bVal = b.profit;
              break;
            case 'registrationDate':
            default:
              aVal = new Date(a.registrationDate);
              bVal = new Date(b.registrationDate);
              break;
          }
          
          if (sortOrder === 'asc') {
            return aVal > bVal ? 1 : -1;
          } else {
            return aVal < bVal ? 1 : -1;
          }
        });

        return {
          referrals: mockReferrals,
          total: 25,
          page,
          totalPages: Math.ceil(25 / limit)
        };
      }

      const params = new URLSearchParams({
        level: level.toString(),
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder
      });

      const response = await api.get(`/referrals/level?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching level referrals:', error);
      throw error;
    }
  }, [api]);

  // Purchase license API call
  const purchaseLicense = useCallback(async (licenseType) => {
    try {
      if (useMockData) {
        // Simulate a successful license purchase
        console.log('Simulating license purchase:', licenseType);
        return {
          success: true,
          license: {
            id: Math.floor(Math.random() * 1000000),
            type: licenseType,
            status: 'active',
            expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            activationDate: new Date().toISOString()
          }
        };
      }

      const response = await api.post('/licenses/purchase', { licenseType });
      return response.data;
    } catch (error) {
      console.error('Error purchasing license:', error);
      throw error;
    }
  }, [api]);

  // Get pricing information including price increase details
  const getPricingInfo = useCallback(async () => {
    try {
      if (useMockData) {
        // Mock pricing info
        console.log('Using mock pricing info');
        return {
          currentPrices: {
            FREE: 0,
            BASIC: 150,
            STANDART: 1250,
            PRO: 3000,
            PREMIUM: 7500,
            VIP: 50000
          },
          futurePrices: {
            FREE: 0,
            BASIC: 180,
            STANDART: 1500,
            PRO: 3600,
            PREMIUM: 9000,
            VIP: 60000
          },
          priceIncreaseInfo: {
            priceIncreaseDate: "2025-06-26T00:00:00.000Z",
            isActive: false,
            daysUntilIncrease: 156,
            increasePercentage: 20,
            message: "Цены увеличатся на 20% через 156 дней (26 июня 2025)"
          }
        };
      }

      const response = await api.get('/licenses/pricing-info');
      return response.data;
    } catch (error) {
      console.error('Error fetching pricing info:', error);
      throw error;
    }
  }, [api]);

  // Get upgrade information
  const getUpgradeInfo = useCallback(async () => {
    try {
      if (useMockData) {
        // Mock upgrade info
        console.log('Using mock upgrade info');
        return {
          currentLicense: 'FREE',
          currentLicensePrice: 0,
          currentBalance: 500,
          availableUpgrades: [
            { licenseType: 'BASIC', fullPrice: 150, upgradePrice: 150, savings: 0, canAfford: true },
            { licenseType: 'STANDART', fullPrice: 1250, upgradePrice: 1250, savings: 0, canAfford: false }
          ],
          priceIncreaseInfo: {
            priceIncreaseDate: "2025-06-26T00:00:00.000Z",
            isActive: false,
            daysUntilIncrease: 156,
            increasePercentage: 20,
            message: "Цены увеличатся на 20% через 156 дней (26 июня 2025)"
          }
        };
      }

      const response = await api.get('/licenses/upgrade-info');
      return response.data;
    } catch (error) {
      console.error('Error fetching upgrade info:', error);
      throw error;
    }
  }, [api]);

  // Auto-trading API calls
  const getAutoTradeSettings = useCallback(async () => {
    try {
      const response = await api.get('/arbitrage/auto-trade/settings');
      return response.data;
    } catch (error) {
      console.error('Error fetching auto-trade settings:', error);
      throw error;
    }
  }, [api]);

  const updateAutoTradeSettings = useCallback(async (settings) => {
    try {
      const response = await api.post('/arbitrage/auto-trade/settings', settings);
      return response.data;
    } catch (error) {
      console.error('Error updating auto-trade settings:', error);
      throw error;
    }
  }, [api]);

  const startAutoTrade = useCallback(async () => {
    try {
      const response = await api.post('/arbitrage/auto-trade/start');
      return response.data;
    } catch (error) {
      console.error('Error starting auto-trade:', error);
      throw error;
    }
  }, [api]);

  const stopAutoTrade = useCallback(async () => {
    try {
      const response = await api.post('/arbitrage/auto-trade/stop');
      return response.data;
    } catch (error) {
      console.error('Error stopping auto-trade:', error);
      throw error;
    }
  }, [api]);

  const getAutoTradeStatus = useCallback(async () => {
    try {
      const response = await api.get('/arbitrage/auto-trade/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching auto-trade status:', error);
      throw error;
    }
  }, [api]);

  // IPN Callback API call
  const processIPNCallback = useCallback(async (paymentId) => {
    try {
      if (useMockData) {
        // Simulate IPN callback processing
        console.log('Simulating IPN callback processing for payment:', paymentId);
        return {
          success: true,
          message: 'Payment processed successfully',
          paymentId,
          status: 'completed'
        };
      }

      const response = await api.post('/transactions/ipn-callback', { payment_id: paymentId });
      return response.data;
    } catch (error) {
      console.error('Error processing IPN callback:', error);
      throw error;
    }
  }, [api]);

  // Admin API calls
  const transferReferral = useCallback(async (userUsername, newReferrerUsername) => {
    try {
      const response = await api.post('/admin/transfer-referral', {
        userUsername,
        newReferrerUsername
      });
      return response.data;
    } catch (error) {
      console.error('Error transferring referral:', error);
      throw error;
    }
  }, [api]);

  const getWithdrawals = useCallback(async () => {
    try {
      const response = await api.get('/admin/withdrawals');
      return response.data;
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      throw error;
    }
  }, [api]);

  const approveWithdrawal = useCallback(async (withdrawalId) => {
    try {
      const response = await api.post(`/admin/withdrawals/${withdrawalId}/approve`);
      return response.data;
    } catch (error) {
      console.error('Error approving withdrawal:', error);
      throw error;
    }
  }, [api]);

  const rejectWithdrawal = useCallback(async (withdrawalId) => {
    try {
      const response = await api.post(`/admin/withdrawals/${withdrawalId}/reject`);
      return response.data;
    } catch (error) {
      console.error('Error rejecting withdrawal:', error);
      throw error;
    }
  }, [api]);

  const giveBonusMoney = useCallback(async (username, amount) => {
    try {
      const response = await api.post('/admin/give-bonus-money', {
        username,
        amount
      });
      return response.data;
    } catch (error) {
      console.error('Error giving bonus money:', error);
      throw error;
    }
  }, [api]);

  const giveBonusDeals = useCallback(async (username, deals) => {
    try {
      const response = await api.post('/admin/give-bonus-deals', {
        username,
        deals
      });
      return response.data;
    } catch (error) {
      console.error('Error giving bonus deals:', error);
      throw error;
    }
  }, [api]);

  const searchUsers = useCallback(async (username) => {
    try {
      const response = await api.get(`/admin/search-users?username=${encodeURIComponent(username)}`);
      return response.data;
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }, [api]);

  // Language API
  const updateUserLanguage = useCallback(async (language) => {
    try {
      const response = await api.post('/user/language', { language });
      return response.data;
    } catch (error) {
      console.error('Error updating user language:', error);
      throw error;
    }
  }, [api]);

  // Convert Fox tokens to USDT
  const convertFoxTokens = useCallback(async (foxAmount) => {
    try {
      const response = await api.post('/user/convert-fox', { foxAmount });
      return response.data;
    } catch (error) {
      console.error('Error converting Fox tokens:', error);
      throw error;
    }
  }, [api]);

  // Convert profit to balance
  const convertProfitToBalance = useCallback(async (profitAmount) => {
    try {
      const response = await api.post('/user/convert-profit', { profitAmount });
      return response.data;
    } catch (error) {
      console.error('Error converting profit to balance:', error);
      throw error;
    }
  }, [api]);

  // Connect exchange API
  const connectExchange = useCallback(async (connectionData) => {
    try {
      const response = await api.post('/user/connect-exchange', connectionData);
      return response.data;
    } catch (error) {
      console.error('Error connecting exchange:', error);
      throw error;
    }
  }, [api]);

  // Get exchange list API
  const getExchangeList = useCallback(async () => {
    try {
      const response = await api.get('/user/exchanges');
      return response.data;
    } catch (error) {
      console.error('Error fetching exchange list:', error);
      throw error;
    }
  }, [api]);

  // Disconnect exchange API
  const disconnectExchange = useCallback(async (exchangeId) => {
    try {
      const response = await api.delete(`/user/exchanges/${exchangeId}`);
      return response.data;
    } catch (error) {
      console.error('Error disconnecting exchange:', error);
      throw error;
    }
  }, [api]);

  return {
    validateTelegramData,
    verifyToken,
    getUserData,
    getTransactions,
    getNotifications,
    initNotificationService,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    createDeposit,
    requestWithdrawal,
    getArbitrageOpportunities,
    executeArbitrage,
    getArbitrageHistory,
    getLicenses,
    getReferralInfo,
    getLevelReferrals,
    purchaseLicense,
    // Auto-trading methods
    getAutoTradeSettings,
    updateAutoTradeSettings,
    startAutoTrade,
    stopAutoTrade,
    getAutoTradeStatus,
    // Payment processing
    processIPNCallback,
    // Admin methods
    transferReferral,
    getWithdrawals,
    approveWithdrawal,
    rejectWithdrawal,
    giveBonusMoney,
    giveBonusDeals,
    searchUsers,
    // Language methods
    updateUserLanguage,
    // New method
    convertFoxTokens,
    // New method
    convertProfitToBalance,
    // Exchange connection
    connectExchange,
    getExchangeList,
    disconnectExchange,
    // Pricing methods
    getPricingInfo,
    getUpgradeInfo
  };
} 